import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD } from '../../permissions/abac-payloads/MartinusAbacPayload.js';
import { usersHaveSameSchoolOnRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { isParentOf } from '../family/isParentOf.js';
import { isSelf } from '../../permissions/api/isSelf.js';
import {
  DYNAMIC_ROLES,
  EMPLOYEE_ROLES,
  Role,
  SBS_ROLES,
  SPENDER_ROLES,
  STUDENT_ROLES,
} from '../../permissions/roles.js';
import type { UserWithPermissionFieldsRoles } from '../../permissions/UserWithPermissionFields.js';
import type { UserOnUserPayload } from '../UserPolicy.js';

export enum UserProfileActions {
  /**
   * See the profile of the viewed user
   */
  VIEW_PROFILE = 'VIEW_PROFILE',

  /**
   * View the general information of the viewed user
   */
  VIEW_GENERAL_INFO = 'VIEW_GENERAL_INFO',

  /**
   * View the address of the viewed user
   */
  VIEW_SENSITIVE_DATA = 'VIEW_SENSITIVE_DATA',

  /**
   * View the bank data of the viewed user
   */
  VIEW_BANK_DATA = 'VIEW_BANK_DATA',

  /**
   * Edit the legacy iban array of a user
   */
  EDIT_LEGACY_IBANS = 'EDIT_LEGACY_IBANS',

  /**
   * Edit the profile of the viewed user
   */
  EDIT_PROFILE = 'EDIT_PROFILE',

  /**
   * Can send the verification mail to a user
   */
  SEND_VERIFICATION_MAIL = 'SEND_VERIFICATION_MAIL',

  /**
   * Can edit the email address of the user
   */
  EDIT_EMAIL = 'EDIT_EMAIL',

  /**
   * Can change the username of the user
   */
  EDIT_USERNAME = 'EDIT_USERNAME',

  /**
   * Can change the username of the user
   */
  EDIT_DEBITORENNUMMER = 'EDIT_DEBITORENNUMMER',

  /**
   * Can change the username of the user
   */
  EDIT_KUERZEL = 'EDIT_KUERZEL',

  /**
   * Can view the last editor of a user
   */
  VIEW_LAST_EDITOR = 'VIEW_LAST_EDITOR',
}

/**
 * Whether `currentUser` sees `viewedUser` as staff: a student, Sorgeberechtigter or Spender of a school where the
 * current user holds one of the roles that work with families - with a global context at every school.
 */
export const seesFamilyMemberAsStaff = (
  currentUser: Maybe<UserWithPermissionFieldsRoles>,
  viewedUser: Maybe<UserWithPermissionFieldsRoles>,
) =>
  usersHaveSameSchoolOnRoles({
    currentUser,
    viewedUser,
    rolesCurrentUser: [
      Role.SPENDEN_ADMIN,
      Role.SCHULLEITER,
      Role.BUCHHALTUNG,
      Role.SEKRETAER,
      Role.SUS_BEWERBER_BEARBEITUNG,
    ],
    rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
  });

const UserProfilePolicyFunctions: Record<UserProfileActions, LoggedInEvaluationFunction<UserOnUserPayload>> = {
  /**
   * A user can see himself and all other users which he has permission on for the school.
   * Dynamic roles are used to determine in which context a user lives
   */
  [UserProfileActions.VIEW_PROFILE]: async request => {
    /**
     * Check if it is the user himself
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Within a family only Sorgeberechtigte open profiles, and only those of their own children. Children, the other
     * parent (think divorce) and Spender see the family on the relations card but cannot open it.
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder sehen.' };
    }

    if (await seesFamilyMemberAsStaff(request.payload.currentUser, request.payload.viewedUser)) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Schüler sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB, Role.MAV];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Mitarbeiter sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },
  /**
   * We can just return the result of the profile check, as the general info is the general part of the profile and everyone who can see the profile can also see the general info
   */
  [UserProfileActions.VIEW_GENERAL_INFO]: async request => {
    const profileResult = await UserProfilePolicyFunctions[UserProfileActions.VIEW_PROFILE](request);
    return profileResult;
  },
  /**
   * Data like the address or the bankinformation is only visible to certain roles. MAV for example can see the general information but no banking information
   */
  [UserProfileActions.VIEW_SENSITIVE_DATA]: async request => {
    /**
     * Check if it is the user himself
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser is a parent of the viewed user
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder sehen.' };
    }

    const allowedRolesOnStudent = [
      Role.SPENDEN_ADMIN,
      Role.SCHULLEITER,
      Role.BUCHHALTUNG,
      Role.SEKRETAER,
      Role.SUS_BEWERBER_BEARBEITUNG,
    ];

    const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnStudent,
      rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
    });
    if (hasCommonContextsStudents) {
      return {
        allowed: true,
        reason: 'Nutzer darf die sensiblen Daten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    /**
     * Check if the currentUser can see the employees sensitive data
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason:
          'Nutzer darf die sensiblen Daten des Mitarbeiters sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer darf die sensiblen Daten des Mitarbeiters nicht sehen.' };
  },

  /**
   * Bankdata can be viewed by the psb for employees for payroll purposes and by spenden_admin and buchhaltung for everyone as they need it for donations.
   */
  [UserProfileActions.VIEW_BANK_DATA]: async request => {
    /**
     * Check if it is the user himself
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser is a parent of the viewed user
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder sehen.' };
    }

    /**
     * Psb on employee neded for pyroles
     */
    const hasCommonContextsEmployees = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: [Role.PSB],
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployees) {
      return {
        allowed: true,
        reason:
          'Nutzer darf die Bankdaten des Mitarbeiters sehen, da er PSB für diesen Nutzer ist und die Daten für die Gehaltsabrechnung benötigt.',
      };
    }

    /**
     * Check if the currentUser can see the employees sensitive data
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.BUCHHALTUNG];

    const hasCommonContextsForAll = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: DYNAMIC_ROLES,
    });
    if (hasCommonContextsForAll) {
      return {
        allowed: true,
        reason:
          'Nutzer darf die sensiblen Daten des Mitarbeiters sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer darf die sensiblen Daten des Mitarbeiters nicht sehen.' };
  },

  /**
   * A user can edit himself and all other users which he has permission on for the school.
   */
  [UserProfileActions.EDIT_PROFILE]: async request => {
    /**
     * Check if it is the user himself
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst bearbeiten.' };
    }

    /**
     * Check if the currentUser is a parent of the viewed user
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder bearbeiten.' };
    }

    /**
     * Check if the currentUser can see the student or sbs
     */
    const allowedRolesOnStudent = [
      Role.SPENDEN_ADMIN,
      Role.SCHULLEITER,
      Role.BUCHHALTUNG,
      Role.SEKRETAER,
      Role.SUS_BEWERBER_BEARBEITUNG,
    ];

    const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnStudent,
      rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
    });
    if (hasCommonContextsStudents) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Schüler bearbeiten. Da er eine bearbeitende Rolle auf der gleichen Schule hat.',
      };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Mitarbeiter bearbeiten. Da er eine bearbeitende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },
  /**
   * At the moment every user who can edit a user can also send the verification mail
   */
  [UserProfileActions.SEND_VERIFICATION_MAIL]: async request => {
    const profileResult = await UserProfilePolicyFunctions[UserProfileActions.EDIT_PROFILE](request);
    return profileResult;
  },
  /**
   * At the moment every user who can edit a user can edit the mail
   */
  [UserProfileActions.EDIT_EMAIL]: async request => {
    const profileResult = await UserProfilePolicyFunctions[UserProfileActions.EDIT_PROFILE](request);
    return profileResult;
  },
  /**
   * At the moment every user who can edit a user can edit the mail
   */
  [UserProfileActions.EDIT_USERNAME]: async request => {
    const profileResult = await UserProfilePolicyFunctions[UserProfileActions.EDIT_PROFILE](request);
    return profileResult;
  },
  /**
   * As changing debitorennummern is a critical action, only privilieged users can do this.
   * The only exception are global psb which can change them for employees so the can cleanup broken data in the database.
   * Maybe we extend it later to global sekretariat for students, but this role does not exist yet.
   */
  [UserProfileActions.EDIT_DEBITORENNUMMER]: async request => {
    const allowedRolesOnUser = [Role.BUCHHALTUNG];

    const hasCommonContextsOnAll = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnUser,
      rolesViewedUser: DYNAMIC_ROLES,
    });

    if (hasCommonContextsOnAll) {
      return {
        allowed: true,
        reason: 'User is Buchhaltung and can edit the debitorennummer of all users of his school.',
      };
    }

    const allowedRolesOnSchuelerAndSbS = [Role.SCHULLEITER, Role.SEKRETAER, Role.SPENDEN_ADMIN];

    const hasCommonContextsOnSchuelerAndSbS = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnSchuelerAndSbS,
      rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
    });

    if (hasCommonContextsOnSchuelerAndSbS) {
      return {
        allowed: true,
        reason: 'User is Schulleiter or Sekretär and can edit the debitorennummer of non employee users of his school.',
      };
    }

    return {
      allowed: false,
      reason: 'User is not global psb',
    };
  },
  /**
   * Changing the Leherekuerzel is also critical as this is used to sync events and schedules form the schools.
   * Only global psb can do this.
   */
  [UserProfileActions.EDIT_KUERZEL]: async request => {
    const userIsGlobalPSB = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.PSB],
      contexts: [GLOBAL_CONTEXT],
    });

    if (!userIsGlobalPSB) {
      return {
        allowed: false,
        reason: 'User is not global psb',
      };
    }

    return {
      allowed: true,
      reason: 'User is global psb.',
    };
  },
  /**
   * Who can view the last editor is reserved for internal editor roles only.
   * Even if the user can edit himself or its children he cannot see the last editor
   */
  [UserProfileActions.VIEW_LAST_EDITOR]: async request => {
    /**
     * Check if the currentUser can see the student or sbs
     */
    const allowedRolesOnStudent = [
      Role.SPENDEN_ADMIN,
      Role.SCHULLEITER,
      Role.BUCHHALTUNG,
      Role.SEKRETAER,
      Role.SUS_BEWERBER_BEARBEITUNG,
    ];

    const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnStudent,
      rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
    });
    if (hasCommonContextsStudents) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Schüler bearbeiten. Da er eine bearbeitende Rolle auf der gleichen Schule hat.',
      };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Mitarbeiter bearbeiten. Da er eine bearbeitende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },

  [UserProfileActions.EDIT_LEGACY_IBANS]: async request => {
    const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: [Role.BUCHHALTUNG],
      rolesViewedUser: DYNAMIC_ROLES,
    });
    if (hasCommonContextsStudents) {
      return {
        allowed: true,
        reason: 'Nutzer ist Buchhaltung und darf die alten IBANs bearbeiten.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht Buchhaltung und darf die alten IBANs nicht bearbeiten.',
    };
  },
};

export const UserProfilePolicy: IPolicy<UserOnUserPayload> = {
  name: 'UserProfilePolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await UserProfilePolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
